import type { ComponentProps, ReactNode } from "react";
import { cx } from "./cx.js";

/**
 * Settings pages share one shape: a section heading above a card, and inside the
 * card one row per setting — what it is on the left, its control or value on the
 * right, a hairline between rows.
 */
export function SettingsSection({ title, description, actions, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("dt-settings-section", className)}>
      {(title || description || actions) && (
        <header className="dt-settings-section__header">
          <div className="dt-settings-section__text">
            {title && <h2 className="dt-settings-section__title">{title}</h2>}
            {description && <p className="dt-settings-section__desc">{description}</p>}
          </div>
          {actions && <div className="dt-settings-section__actions">{actions}</div>}
        </header>
      )}
      <div className="dt-settings-card">{children}</div>
    </section>
  );
}

export interface SettingsRowProps extends Omit<ComponentProps<"div">, "children"> {
  label: ReactNode;
  /** Id of the control on the right, so the label names it. */
  labelFor?: string;
  description?: ReactNode;
  error?: string | null | undefined;
  /** The control or value on the right. */
  children?: ReactNode;
}

export function SettingsRow({ label, labelFor, description, error, children, className, ...props }: SettingsRowProps) {
  return (
    <div className={cx("dt-settings-row", className)} {...props}>
      <div className="dt-settings-row__text">
        {labelFor ? (
          <label htmlFor={labelFor} className="dt-settings-row__label">
            {label}
          </label>
        ) : (
          <div className="dt-settings-row__label">{label}</div>
        )}
        {description && <div className="dt-settings-row__desc">{description}</div>}
        {error && (
          <p className="dt-settings-row__error" role="alert">
            {error}
          </p>
        )}
      </div>
      {children != null && children !== false && <div className="dt-settings-row__control">{children}</div>}
    </div>
  );
}

/** Content that is not a setting — instructions, a notice, an empty state — padded like a row. */
export function SettingsBlock({ className, ...props }: ComponentProps<"div">) {
  return <div className={cx("dt-settings-block", className)} {...props} />;
}

/** Save and friends, under the card they act on. */
export function SettingsActions({ className, ...props }: ComponentProps<"div">) {
  return <div className={cx("dt-settings-actions", className)} {...props} />;
}
